import React from "react";
import type {DestinationKey} from "nav/appDestinations";

/**
 * Inline SVG icons for the logged-in navigation (originally the ticket #28 rail, reused by the top
 * NavBar's icon-only mobile pill in ui#96 and by every ProfileMenu row since ui#520), drawn to the
 * shared icon conventions (`home/components/icons.tsx`, `download/recipient/icons.tsx`): 24-unit
 * viewBox, 2px rounded strokes, `currentColor` fills nothing — the nav's ink tokens flow through the
 * stroke.
 *
 * Every glyph is `aria-hidden`, without exception: these are DECORATION beside a text label that is
 * already the control's accessible name. A glyph that exposed a name of its own would either shadow
 * that label or append to it, which is how a menu ends up announcing "Contacts Contacts".
 *
 * The set is shared on purpose. The pill and the ProfileMenu show the SAME item at complementary
 * widths (ui#451), so a second drawing of, say, Branding would let the two surfaces drift into
 * different pictures for one destination.
 */

interface IconProps {
	size?: number;
}

/** Shared svg shell so every rail glyph carries the exact same stroke treatment. */
function RailGlyph({size = 20, children}: IconProps & {children: React.ReactNode}) {
	return (
		<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
			 strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
			{children}
		</svg>
	);
}

/** Home destination — roofline house. */
export function HomeIcon({size}: IconProps) {
	return (
		<RailGlyph size={size}>
			<path d="m3 10.5 9-7.5 9 7.5"/>
			<path d="M5.5 8.5V20h13V8.5"/>
			<path d="M10 20v-5h4v5"/>
		</RailGlyph>
	);
}

/** Transfers destination — both-ways arrows. */
export function TransfersIcon({size}: IconProps) {
	return (
		<RailGlyph size={size}>
			<path d="M4 8h15"/>
			<path d="m15.5 4.5 3.5 3.5-3.5 3.5"/>
			<path d="M20 16H5"/>
			<path d="m8.5 12.5L5 16l3.5 3.5"/>
		</RailGlyph>
	);
}

/** Contacts destination — two-person group. */
export function ContactsIcon({size}: IconProps) {
	return (
		<RailGlyph size={size}>
			<circle cx="9" cy="8" r="3.5"/>
			<path d="M3.5 20v-.5a5.5 5.5 0 0 1 11 0v.5"/>
			<path d="M15.5 4.8a3.5 3.5 0 0 1 0 6.4"/>
			<path d="M17.6 14.3a5.5 5.5 0 0 1 2.9 4.8v.9"/>
		</RailGlyph>
	);
}

/**
 * Branding — a paint drop, for the logo + accent colour the sheet edits (ui#450).
 *
 * Lives here rather than in `NavBar.tsx` because Branding has TWO render sites since ui#451 — the
 * pill and, once the pill collapses, the ProfileMenu row — and they must show one picture.
 */
export function BrandingIcon({size}: IconProps) {
	return (
		<RailGlyph size={size}>
			<path d="M12 3.5c3.2 3.4 5.5 6.3 5.5 9a5.5 5.5 0 0 1-11 0c0-2.7 2.3-5.6 5.5-9z"/>
		</RailGlyph>
	);
}

/* The ProfileMenu quick actions (ui#520). They have no counterpart in the pill, so unlike the
   destinations above they are drawn here for the menu alone — to the same stroke conventions, so the
   menu reads as one icon system rather than two sets meeting at a divider. */

/** Upgrade — an arrow rising inside a circle: moving up a tier. */
export function UpgradeIcon({size}: IconProps) {
	return (
		<RailGlyph size={size}>
			<circle cx="12" cy="12" r="9"/>
			<path d="M12 16.5v-9"/>
			<path d="m8.5 11 3.5-3.5 3.5 3.5"/>
		</RailGlyph>
	);
}

/** Account — a SINGLE person, deliberately distinct from the two-person {@link ContactsIcon}. */
export function AccountIcon({size}: IconProps) {
	return (
		<RailGlyph size={size}>
			<circle cx="12" cy="8" r="3.5"/>
			<path d="M4.5 20v-.5a7.5 7.5 0 0 1 15 0v.5"/>
		</RailGlyph>
	);
}

/**
 * Analytics — a bar chart on its baseline (ui#1522), drawn in the same rail stroke family as the rows
 * it sits between. Bars rather than a line or a pie because that is what the embedded dashboard the
 * row opens actually shows, and because neither of the other two would read at 16px.
 */
export function AnalyticsIcon({size}: IconProps) {
	return (
		<RailGlyph size={size}>
			<path d="M4 20V10"/>
			<path d="M10 20V4"/>
			<path d="M16 20v-7"/>
			<path d="M21 20H3"/>
		</RailGlyph>
	);
}

/**
 * Billing — a payment card with its magnetic stripe (ui#1791), in the same rail stroke family as the
 * rows around it. A card rather than a receipt or a currency mark: the sheet's own subject is the plan
 * and the payment method behind it, and a currency glyph would have to pick a currency.
 */
export function BillingIcon({size}: IconProps) {
	return (
		<RailGlyph size={size}>
			<rect x="3" y="6" width="18" height="12" rx="2"/>
			<path d="M3 10h18"/>
			<path d="M7 14.5h3"/>
		</RailGlyph>
	);
}

/* AdvertiseIcon is gone with the ProfileMenu row it drew (owner 2026-08-17). The Resources menu's
   Advertising column uses the MUI outlined set its sibling rows already draw from, not this rail
   stroke family, so keeping the glyph here would have left an export nothing renders. */

/** Admin (ui#1882): a shield with a check, the operator console's gate rather than a key. */
export function AdminIcon({size}: IconProps) {
	return (
		<RailGlyph size={size}>
			<path d="M12 3l7 3v5c0 4.6-3 8.6-7 10-4-1.4-7-5.4-7-10V6l7-3z"/>
			<path d="m9 12 2 2 4-4"/>
		</RailGlyph>
	);
}

/** Logout — leaving the enclosure through the door on the right. */
export function LogoutIcon({size}: IconProps) {
	return (
		<RailGlyph size={size}>
			<path d="M10 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h4"/>
			<path d="m15 8 4 4-4 4"/>
			<path d="M19 12H9"/>
		</RailGlyph>
	);
}

/**
 * The glyph for each logged-in destination, keyed by the destination's own stable key.
 *
 * ONE table, read by both surfaces that render a destination: the top-bar pill and — below the ui#451
 * thresholds, where the pill hands the item over — the ProfileMenu row. Keyed by `key` rather than by
 * `path` so a route change cannot silently drop an item's icon (a missing key is a compile error;
 * a missing path was `undefined`).
 */
export const DESTINATION_ICONS: Record<DestinationKey, React.ReactNode> = {
	home: <HomeIcon/>,
	transfers: <TransfersIcon/>,
	contacts: <ContactsIcon/>,
	// Personalization became a destination in ui#1292. Neither surface reads its glyph from HERE — the
	// pill draws {@link BrandingIcon} itself at the shared pill glyph size (it is a toggle button, not a
	// mapped link) and the ProfileMenu's collapsed row does the same — but the entry is the same glyph
	// rather than a placeholder, so a future table-driven surface renders the item correctly instead of
	// rendering something else.
	personalization: <BrandingIcon/>,
	// Account never appears in the pill (it lives in the ProfileMenu), but it is a destination, and
	// leaving it out would make this table partial in a way the type no longer catches.
	account: <AccountIcon/>,
};

